import { useCallback, useMemo } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  MonitoringEntitlement,
  MonitoringEntitlementAction,
  MonitoringEntitlementChange,
} from '@/types/monitoring';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/**
 * Slow: entitlements change when an admin clicks something, and the page
 * refetches straight after its own changes. The poll only picks up another
 * admin's change to the same screen.
 */
export const ENTITLEMENTS_POLL_MS = 60_000;

const EMPTY: MonitoringEntitlement[] = [];

/**
 * Every team's monitoring entitlement, keyed by team id, and the actions that
 * change one.
 *
 * Actions never toast: the caller decides how to present a partial result,
 * which matters here because a suspend can commit locally while the provider
 * keeps billing — that has to read differently from success.
 */
export function useMonitoringEntitlements(options?: { enabled?: boolean }) {
  const poll = usePoll<MonitoringEntitlement[]>({
    enabled: options?.enabled !== false,
    interval: ENTITLEMENTS_POLL_MS,
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listMonitoringEntitlements({
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { refetch } = poll;
  const list = poll.data ?? EMPTY;

  const byTeam = useMemo(
    () => Object.fromEntries(list.map((row) => [row.teamId, row])),
    [list]
  ) as Record<string, MonitoringEntitlement | undefined>;

  const saveDetails = useCallback(
    async (
      teamId: string,
      details: { contractRef: string | null; notes: string | null }
    ): Promise<MonitoringEntitlement> => {
      const saved = await vnextApiClient.putMonitoringEntitlement(teamId, details);
      await refetch();
      return saved;
    },
    [refetch]
  );

  const change = useCallback(
    async (
      teamId: string,
      action: MonitoringEntitlementAction
    ): Promise<MonitoringEntitlementChange> => {
      try {
        return await vnextApiClient.changeMonitoringEntitlement(teamId, action);
      } finally {
        // Also on failure: a 409 means someone else changed it, and the row
        // on screen is the stale one.
        await refetch();
      }
    },
    [refetch]
  );

  return {
    entitlements: byTeam,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
    saveDetails,
    change,
  };
}
